/**
 * Utility functions for determining product order availability status (preorder/backorder)
 */

/**
 * Determines if a product is available for preorder
 * @param {Object} product - The product object
 * @returns {boolean} - True if product is in preorder status
 */
export const isPreorder = (product) => {
    return !!product?.c_isPreorder
}

/**
 * Determines if a product is available for backorder
 * @param {Object} product - The product object
 * @returns {boolean} - True if product is in backorder status
 */
export const isBackorder = (product) => {
    return !!product?.c_isBackorder
}

/**
 * Gets the future stock date formatted (used for both preorder and backorder)
 * @param {Object} product - The product object
 * @param {string} locale - The locale for date formatting (e.g., 'en-GB')
 * @returns {string|null} - Formatted date string or null
 */
export const getFutureStockDate = (product, locale = 'it-IT') => {
    const dateString = product?.inventory?.inStockDate

    if (!dateString) {
        return null
    }

    try {
        const futureStockDate = new Date(dateString)
        return futureStockDate.toLocaleDateString(locale, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
        })
    } catch (error) {
        console.error('Error formatting future stock date:', error)
        return null
    }
}

/**
 * Determines the order availability status of a product
 * @param {Object} product - The product object
 * @returns {Object} - Object containing status and relevant data
 */
export const getOrderAvailabilityStatus = (product) => {
    const preorder = isPreorder(product)
    const backorder = isBackorder(product)

    return {
        isPreorder: preorder,
        isBackorder: backorder,
        isStandardOrder: !preorder && !backorder,
        futureStockDate: preorder || backorder ? product?.inventory?.inStockDate : null,
    }
}
